import type { CommandResult, CommandWire, ContextOrg, FileBucket, PullResponse, SubmitResponse } from '@gk/contracts'
import type { RefreshOutcome, Session } from '../auth/client.ts'
import { publicOrigin, serverUrl } from '../platform/origin.ts'

/** How a whole-call failure is classified (ARCHITECTURE 9.4). The engine decides what to do from `kind` alone. */
export type TransportErrorKind =
  | 'network'    // no answer: retry the same batch later, unchanged (idempotent)
  | 'timeout'    // 57014: the server cancelled the whole call: halve the batch
  | 'auth'       // 401 / expired JWT: refresh once, outbox untouched
  | 'frozen'     // api schema not exposed (restore in progress, PGRST106) or 503: wait
  | 'protocol'   // 22023 BATCH_INVALID, malformed: a bug, never retried blindly
  | 'server'     // anything else
export class TransportError extends Error {
  constructor(public kind: TransportErrorKind, message: string, public code?: string) { super(message) }
}

export interface Transport {
  submit(deviceId: string, epoch: string, commands: CommandWire[]): Promise<SubmitResponse>
  markTimeout(deviceId: string, command: CommandWire): Promise<CommandResult>
  pull(orgId: string, cursor: number, epoch: string | null): Promise<PullResponse>
  heartbeat(deviceId: string, orgId: string, info: HeartbeatInfo): Promise<void>
  myContext(): Promise<ContextOrg[]>
  /** Office-only review reads for the import wizard, scoped to the club on screen (M23-0, docs/M23.md item 31: a person of two clubs saw the other
   *  club's batches); RLS still decides what a caller sees, and a batch of another club is refused SCOPE_DENIED. */
  importGet(orgId: string, batchId: string, afterRow: number, limit: number): Promise<ImportView>
  importList(orgId: string): Promise<ImportListItem[]>
  /** Right after a second-factor challenge: a single-use nonce (valid 10 min) for ONE online-only command (release, downgrade). */
  beginOnlineAction(orgId: string, deviceId: string): Promise<string>
  /** Workshop reads (online): what is due, and the lifetime view of each meter. RLS decides what a caller sees. */
  serviceDue(assetId?: string): Promise<ServiceDueRow[]>
  meterStatus(assetId?: string): Promise<MeterStatusRow[]>
  /** Any read or privileged RPC of schema `api` (the people, access and kiosk screens). A business refusal throws a TransportError whose message is the code. */
  rpc<T = unknown>(fn: string, args: Record<string, unknown>): Promise<T>
  /** A short-lived signed URL for a PUBLISHED map archive in the private `maps` bucket (storage's own policy decides who may ask). */
  mapFileUrl(storageKey: string): Promise<string>
  /** Upload a registered photo under the device's own login (storage policy: only the key of a pending file of a site it may write). `upsert: false`; "already there" counts as success.
   *  M15-V: `bucket` names the documents bucket for a PDF or a vault file (the link and the blob row say which; the photos' bucket when they say nothing). */
  uploadAttachment(storageKey: string, blob: Blob, contentType: string, bucket?: FileBucket): Promise<'ok' | 'exists'>
  /** A short-lived signed URL for a photo of the site (storage's own policy decides who may ask); M15-V: or for a file of the documents bucket. */
  attachmentUrl(storageKey: string, bucket?: FileBucket): Promise<string>
  refreshAuth(): Promise<boolean>
}
export interface ImportBatchView {
  id: string; file_name: string; status: 'staging' | 'staged' | 'committed'; total_rows: number; rows_received: number
  rows_sha256: string | null; created_asset_count: number | null; committed_at: string | null; duplicate_content_of: string | null
  mapping: Record<string, string>; kind_map: Record<string, string>; defaults: Record<string, string>
}
export interface ImportRowView {
  row_no: number; cells: Record<string, string>; normalised: Record<string, string | number | string[] | null>; flags: string[]
  duplicate_of_asset_id: string | null; duplicate_of_tag: string | null; duplicate_in_file_row: number | null
  decision: 'create' | 'skip' | null; created_asset_id: string | null
}
export interface ImportView { batch: ImportBatchView | null; rows: ImportRowView[] }
export type DueStatus = 'overdue' | 'due' | 'due_soon' | 'no_anchor' | 'no_meter' | 'ok'
export interface ServiceDueRow {
  asset_id: string; plan_id: string; plan_version_id: string; plan_name: string; trigger_logic: string; status: DueStatus; reached_by: string[]
  last_event_id: string | null; last_service_at: string | null; calendar_due_on: string | null; days_remaining: number | null
  meter_slot_id: string | null; meter_unit: string | null; lifetime_value: number | null; meter_due_at: number | null; meter_remaining: number | null
}
export interface MeterStatusRow {
  org_id: string; site_id: string; asset_id: string; slot_id: string; kind: string; unit_code: string; open_instance_id: string | null
  current_value: number | null; lifetime_value: number; opening_value: number | null; pending_count: number
}
export interface ImportListItem { id: string; file_name: string; status: string; total_rows: number; rows_received: number; created_asset_count: number | null; created_at: string; committed_at: string | null }
/** api.work_order_downtime's answer (M23 item 44, migration 20261126100500; read through `rpc('work_order_downtime', { p_org, p_work_order })`): whole seconds;
 *  `*_open` when a restriction is still in force or a timer still runs. */
export interface WorkOrderDowntime {
  work_order_id: string; state: string; as_of: string
  restrictions: number; downtime_seconds: number; downtime_open: boolean
  sessions: number; labour_seconds: number; labour_pending_seconds: number; labour_open: boolean
}
export interface HeartbeatInfo {
  outbox_depth: number; oldest_pending_at: string | null; storage_persisted: boolean | null
  standalone: boolean | null; app_version: string; last_cursor: number; clock_offset_ms: number
}

type PgError = { message?: string; code?: string; details?: string; hint?: string } | null
export function classify(error: PgError, status?: number): TransportError {
  const code = error?.code ?? ''
  const msg = error?.message ?? 'error'
  if (code === '57014') return new TransportError('timeout', msg, code)
  if (status === 401 || code === 'PGRST301' || code === 'PGRST303' || /jwt/i.test(msg)) return new TransportError('auth', msg, code)
  if (code === 'PGRST106' || status === 503 || status === 502) return new TransportError('frozen', msg, code)
  if (code === '22023') return new TransportError('protocol', msg, code)
  if (status === 0 || !status && /fetch|network|offline|load failed|failed to/i.test(msg)) return new TransportError('network', msg, code)
  return new TransportError('server', msg, code)
}

/** What the transport needs of the auth client (src/auth/client.ts): a token to send, a refresh, and the news that a session was stored. */
export interface TransportAuth {
  accessToken(): Promise<string | null>
  refresh(): Promise<RefreshOutcome>
  onChange(cb: (s: Session | null) => void): () => void
}
export interface RestTransportOptions { fetchImpl?: typeof fetch; origin?: () => string | null }

/** The code the engine sees instead of a 401 while the auth service cannot be reached (9.3): it backs off and retries by itself. */
export const AUTH_SERVICE_UNAVAILABLE = 'AUTH_SERVICE_UNAVAILABLE'

const errorBody = (j: unknown, status: number): PgError => {
  if (j && typeof j === 'object' && !Array.isArray(j)) {
    const o = j as Record<string, unknown>
    const message = typeof o.message === 'string' ? o.message : typeof o.error === 'string' ? o.error : `HTTP ${status}`
    return { message, ...(typeof o.code === 'string' ? { code: o.code } : typeof o.error === 'string' ? { code: o.error } : {}) }
  }
  return { message: `HTTP ${status}` }
}

/** PostgREST and file-service transport over `fetch` (M22, docs/STACK.md 9.4; it replaces supabase-js): one RPC per call into schema `api` (the only
 *  exposed one) at `/rest/v1/rpc/<fn>` with the person's access token, and the file calls at `/files`, all on the club's public origin (the page's own
 *  in a browser: same origin everywhere). Nothing here knows a business rule.
 *  A refresh that failed for a TRANSIENT reason (the app server restarting during a deploy while PostgREST answers) is remembered: until a refresh
 *  succeeds, a 401 from /rest/v1 or /files is `frozen` (AUTH_SERVICE_UNAVAILABLE), so the engine backs off and retries instead of asking for a
 *  sign-in (9.3); the client's own ticker keeps trying the refresh. Only a DEFINITIVE refusal makes `refreshAuth()` answer false. */
export function restTransport(auth: TransportAuth, o: RestTransportOptions = {}): Transport {
  const fetchImpl = o.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a))
  const origin = o.origin ?? publicOrigin
  let refreshTransient = false
  auth.onChange((s) => { if (s) refreshTransient = false })            // a session was stored: a refresh (or a sign-in) succeeded

  const urlOf = (path: string): string => {
    const u = serverUrl(path, origin())
    if (!u) throw new TransportError('server', 'NO_PUBLIC_ORIGIN', 'NO_PUBLIC_ORIGIN')
    return u
  }
  const refusal = (j: unknown, status: number): TransportError => {
    const e = classify(errorBody(j, status), status)
    return e.kind === 'auth' && refreshTransient ? new TransportError('frozen', AUTH_SERVICE_UNAVAILABLE, AUTH_SERVICE_UNAVAILABLE) : e
  }
  async function bearer(): Promise<string> {
    const token = await auth.accessToken()
    if (!token) throw new TransportError('auth', 'NOT_AUTHENTICATED', 'NOT_AUTHENTICATED')
    return token
  }
  async function send(path: string, init: RequestInit): Promise<{ status: number; json: unknown }> {
    const url = urlOf(path)
    let r: Response
    try { r = await fetchImpl(url, { ...init, credentials: 'omit', cache: 'no-store' }) } catch (e) { throw new TransportError('network', e instanceof Error ? e.message : String(e)) }
    let text: string
    try { text = await r.text() } catch (e) { throw new TransportError('network', e instanceof Error ? e.message : String(e)) }
    let json: unknown = null
    if (text) { try { json = JSON.parse(text) } catch { json = null } }
    return { status: r.status, json }
  }
  async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
    const token = await bearer()
    const r = await send(`/rest/v1/rpc/${fn}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Content-Profile': 'api', Authorization: `Bearer ${token}` }, body: JSON.stringify(args),
    })
    if (r.status < 200 || r.status >= 300) throw refusal(r.json, r.status)
    return r.json as T                                                   // 204 or an empty body: null
  }
  /** POST /files/sign: a short-lived signed path for one object the caller may read (the storage policies decide), as an absolute URL on the public origin. */
  async function signed(bucket: 'maps' | FileBucket, key: string): Promise<string> {
    const token = await bearer()
    const r = await send('/files/sign', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ bucket, key }) })
    const path = r.status === 200 && r.json && typeof (r.json as { signedPath?: unknown }).signedPath === 'string' ? (r.json as { signedPath: string }).signedPath : null
    if (!path) throw r.status === 200 ? new TransportError('server', 'no signed url') : refusal(r.json, r.status)
    return urlOf(path)
  }
  return {
    submit: (deviceId, epoch, commands) => call<SubmitResponse>('submit_commands', { p_device_id: deviceId, p_epoch: epoch, p_commands: commands }),
    markTimeout: (deviceId, command) => call<CommandResult>('mark_command_timeout', { p_device_id: deviceId, p_command: command }),
    pull: (orgId, cursor, epoch) => call<PullResponse>('sync_pull', { p_org_id: orgId, p_cursor: cursor, p_epoch: epoch }),
    async heartbeat(deviceId, orgId, i) {
      await call<null>('report_heartbeat', {
        p_device_id: deviceId, p_outbox_depth: i.outbox_depth, p_oldest_pending_at: i.oldest_pending_at,
        p_storage_persisted: i.storage_persisted, p_standalone: i.standalone, p_app_version: i.app_version,
        p_last_cursor: i.last_cursor, p_clock_offset_ms: i.clock_offset_ms, p_org_id: orgId,
      })
    },
    myContext: () => call<ContextOrg[]>('my_context', {}),
    importGet: (orgId, batchId, afterRow, limit) => call<ImportView>('import_get', { p_org_id: orgId, p_batch_id: batchId, p_after_row: afterRow, p_limit: limit }),
    importList: (orgId) => call<ImportListItem[]>('import_list', { p_org_id: orgId }),
    beginOnlineAction: async (orgId, deviceId) => (await call<{ nonce: string }>('begin_online_action', { p_org_id: orgId, p_device_id: deviceId })).nonce,
    rpc: <T,>(fn: string, args: Record<string, unknown>) => call<T>(fn, args),
    mapFileUrl: (storageKey) => signed('maps', storageKey),
    /** POST /files/<attachments | documents>/<key>, the bytes as the body (fetch sets Content-Length): 201 ok, 409 "already there" (also a success: the key is the
     *  content's hash and nothing is ever overwritten); a 401 is `auth` (or `frozen`, above); 502/503 (busy, restarting) are `frozen`, never a failure. */
    async uploadAttachment(storageKey, blob, contentType, bucket = 'attachments') {
      const token = await bearer()
      const r = await send(`/files/${bucket}/${storageKey}`, { method: 'POST', headers: { 'Content-Type': contentType, Authorization: `Bearer ${token}` }, body: blob })
      if (r.status === 201 || r.status === 200) return 'ok'
      if (r.status === 409) return 'exists'
      throw refusal(r.json, r.status)
    },
    attachmentUrl: (storageKey, bucket = 'attachments') => signed(bucket, storageKey),
    serviceDue: (assetId) => call<ServiceDueRow[]>('service_due', { p_asset: assetId ?? null }),
    meterStatus: (assetId) => call<MeterStatusRow[]>('meter_status', { p_asset: assetId ?? null }),
    async refreshAuth() {
      const r = await auth.refresh()
      refreshTransient = r === 'transient'
      return r !== 'refused'
    },
  }
}
